import { useState, useEffect } from 'react';
import { brandsApi, Brand } from '../../api/brands.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';

const BrandList = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setIsLoading(true);
      const data = await brandsApi.getAll();
      setBrands(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Markalar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (brand?: Brand) => {
    if (brand) {
      setEditingBrand(brand);
      setFormData({
        name: brand.name,
        description: brand.description || '',
      });
    } else {
      setEditingBrand(null);
      setFormData({
        name: '',
        description: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBrand(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingBrand) {
        await brandsApi.update(editingBrand.id, formData);
      } else {
        await brandsApi.create(formData);
      }
      await fetchBrands();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu markayı silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await brandsApi.delete(id);
      await fetchBrands();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  const columns: Column<Brand>[] = [
    { key: 'name', header: 'Marka Adı' },
    { key: 'description', header: 'Açıklama', render: (item) => item.description || '-' },
    {
      key: 'isActive',
      header: 'Durum',
      render: (item) => (
        <span
          className={`px-2 py-1 text-xs rounded-full ${
            item.isActive
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {item.isActive ? 'Aktif' : 'Pasif'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Markalar yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Markalar</h1>
        <Button onClick={() => handleOpenModal()}>Yeni Marka Ekle</Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <Table data={brands} columns={columns} emptyMessage="Henüz marka bulunmamaktadır" />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingBrand ? 'Marka Düzenle' : 'Yeni Marka Ekle'}
      >
        <form onSubmit={handleSubmit}>
          <Input
            label="Marka Adı"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Açıklama"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              {editingBrand ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default BrandList;
